import { apiTryFetchRetry } from '@editor/utils/apiFetchRetry';
import { KittlHeaderNames } from '@repo/api-client';

import { Design, Folder, GenericApiResponse } from '../types';

import { USER_API_URL } from './constants';

export type FolderResponse = GenericApiResponse<{ folder: Folder }>;

export type GetFoldersResponse = GenericApiResponse<{
  results: Folder[];
  total: number;
  skip: number;
  take: number;
}>;

type RemoveFromFolderResponse = GenericApiResponse<{ design: Design }>;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createAPI = ({ url }: { url: string }) => {
  const getFolder = async ({ id }: { id: string }): Promise<FolderResponse> => {
    return await apiTryFetchRetry<FolderResponse>(`${url}/${id}`, {
      method: 'Get',
    });
  };

  /**
   *
   * @param options { name, workspaceId } - name of the folder and workspaceId. Note, workspaceId is only needed currently from the website as it
   * is pulled in automatically by the Editor in the `apiTryFetchRetry` function.
   * @returns
   */
  const createFolder = async ({
    name = 'New Folder',
    workspaceId,
  }: { name?: string; workspaceId?: string } = {}): Promise<FolderResponse> => {
    const headers: Record<string, string> = workspaceId
      ? { [KittlHeaderNames.WORKSPACE_ID]: workspaceId }
      : {};
    return await apiTryFetchRetry<FolderResponse>(`${url}/create`, {
      method: 'POST',
      body: JSON.stringify({ name }),
      headers,
    });
  };

  const updateFolder = async ({
    id,
    name,
  }: {
    id: string;
    name: string;
  }): Promise<FolderResponse> => {
    return await apiTryFetchRetry<FolderResponse>(`${url}/update`, {
      method: 'POST',
      body: JSON.stringify({ id, name }),
    });
  };

  const deleteFolder = async ({
    id,
  }: {
    id: string;
  }): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry<GenericApiResponse>(`${url}/delete`, {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
  };

  const moveToFolder = async (
    folderId: string,
    designIds: string[],
    workspaceId?: string
  ): Promise<GenericApiResponse> => {
    const headers: Record<string, string> = workspaceId
      ? { [KittlHeaderNames.WORKSPACE_ID]: workspaceId }
      : {};
    return await apiTryFetchRetry<GenericApiResponse>(
      `${url}/${folderId}/move_to`,
      {
        method: 'POST',
        body: JSON.stringify({ designIds }),
        headers,
      }
    );
  };

  const removeFromFolder = async (
    folderId: string,
    designId: string
  ): Promise<RemoveFromFolderResponse> => {
    return await apiTryFetchRetry<RemoveFromFolderResponse>(
      `${url}/${folderId}/remove`,
      {
        method: 'POST',
        body: JSON.stringify({ designId }),
      }
    );
  };

  const getFolders = async (options?: {
    take?: number;
    skip?: number;
    sort?: string;
    workspaceId?: string;
  }): Promise<GetFoldersResponse> => {
    const { take = 10, skip = 0, sort, workspaceId } = options || {};
    const headers: Record<string, string> = workspaceId
      ? { [KittlHeaderNames.WORKSPACE_ID]: workspaceId }
      : {};

    return await apiTryFetchRetry<GetFoldersResponse>(
      `${url}/index?take=${take}&skip=${skip}${sort ? `&sort=${sort}` : ''}`,
      { method: 'GET', headers }
    );
  };

  const searchFolders = async (
    take: number,
    skip: number,
    query: string,
    workspaceId?: string
  ): Promise<GetFoldersResponse> => {
    const headers: Record<string, string> = workspaceId
      ? { [KittlHeaderNames.WORKSPACE_ID]: workspaceId }
      : {};

    return await apiTryFetchRetry<GetFoldersResponse>(
      `${url}/search?take=${take}&skip=${skip}&query=${query}`,
      { method: 'GET', headers }
    );
  };

  return {
    getFolder,
    createFolder,
    updateFolder,
    deleteFolder,
    moveToFolder,
    removeFromFolder,
    getFolders,
    searchFolders,
  };
};

const folderApi = createAPI({ url: `${USER_API_URL}/folders` });
export default folderApi;
